import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  Gauge,
  Globe,
  Inbox,
  Lock,
  MousePointerClick,
  Shield,
  ShieldCheck,
  TrendingUp,
  Users,
  Wrench,
} from "lucide-react";
import { sb } from "@/lib/sb";

export const Route = createFileRoute("/_authenticated/dashboard/admin/")({
  component: AdminOverview,
});

type AnalyticsRow = {
  id: string;
  path: string;
  referrer: string | null;
  session_id: string | null;
  created_at: string;
};

type LeadRow = {
  id: string;
  name: string | null;
  email: string | null;
  status: string | null;
  created_at: string;
};

type VitalRow = {
  metric: string;
  value: number;
};

type SecurityEvent = {
  event_type: string;
  severity: string | null;
  created_at: string;
};
type UptimeRow = { is_up: boolean; response_ms: number | null; checked_at: string };
type SslRow = { domain: string; valid_to: string | null; days_remaining: number | null; checked_at: string };
type PagespeedRow = {
  strategy: string;
  performance: number | null;
  accessibility: number | null;
  best_practices: number | null;
  seo: number | null;
  lcp_ms: number | null;
  cls: number | null;
  tbt_ms: number | null;
  checked_at: string;
};



const RANGES = [
  { k: 7, l: "7 days" },
  { k: 30, l: "30 days" },
  { k: 90, l: "90 days" },
] as const;

const PIE_COLORS = ["hsl(var(--primary))", "#60a5fa", "#a78bfa", "#34d399", "#fbbf24", "#f87171"];

const referrerBucket = (ref: string | null) => {
  if (!ref) return "Direct";
  try {
    const host = new URL(ref).hostname.replace(/^www\./, "");
    if (host.includes("google")) return "Google";
    if (host.includes("bing") || host.includes("duckduckgo") || host.includes("yahoo")) return "Other Search";
    if (host.includes("facebook") || host.includes("instagram") || host.includes("linkedin") || host.includes("twitter") || host.includes("x.com") || host.includes("t.co")) return "Social";
    if (host.includes("homesteadconstruction")) return "Direct";
    return host;
  } catch {
    return "Direct";
  }
};

const percentile = (vals: number[], p: number) => {
  if (!vals.length) return 0;
  const sorted = [...vals].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
};

function AdminOverview() {
  const [days, setDays] = useState(30);
  const [analyticsRows, setAnalyticsRows] = useState<AnalyticsRow[]>([]);
  const [leadRows, setLeadRows] = useState<LeadRow[]>([]);
  const [vitalRows, setVitalRows] = useState<VitalRow[]>([]);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [uptimeRows, setUptimeRows] = useState<UptimeRow[]>([]);
  const [sslRows, setSslRows] = useState<SslRow[]>([]);
  const [pagespeedRows, setPagespeedRows] = useState<PagespeedRow[]>([]);
  const [runningPsi, setRunningPsi] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const since = new Date(Date.now() - days * 86400e3).toISOString();
      const since30 = new Date(Date.now() - 30 * 86400e3).toISOString();
      const [{ data: analytics }, { data: leads }, { data: vitals }, { data: security }, { data: uptime }, { data: ssl }, { data: psi }] = await Promise.all([
        sb.from("page_analytics").select("id, path, referrer, session_id, created_at").gte("created_at", since).limit(10000),
        sb.from("form_submissions").select("id, name, email, status, created_at").gte("created_at", since).order("created_at", { ascending: false }).limit(200),
        sb.from("web_vitals").select("metric, value").gte("ts", since).limit(5000),
        sb.from("security_events").select("event_type, severity, created_at").gte("created_at", since).limit(500),
        sb.from("uptime_checks").select("is_up, response_ms, checked_at").gte("checked_at", since30).order("checked_at", { ascending: false }).limit(5000),
        sb.from("ssl_checks").select("domain, valid_to, days_remaining, checked_at").order("checked_at", { ascending: false }).limit(50),
        sb.from("pagespeed_audits").select("strategy, performance, accessibility, best_practices, seo, lcp_ms, cls, tbt_ms, checked_at").order("checked_at", { ascending: false }).limit(20),
      ]);

      setAnalyticsRows((analytics as AnalyticsRow[]) ?? []);
      setLeadRows((leads as LeadRow[]) ?? []);
      setVitalRows((vitals as VitalRow[]) ?? []);
      setSecurityEvents((security as SecurityEvent[]) ?? []);
      setUptimeRows((uptime as UptimeRow[]) ?? []);
      setSslRows((ssl as SslRow[]) ?? []);
      setPagespeedRows((psi as PagespeedRow[]) ?? []);

      setLoading(false);
    };



    load();
  }, [days]);

  const stats = useMemo(() => {
    const views = analyticsRows.length;
    const sessions = new Set(analyticsRows.map((r) => r.session_id || r.id)).size;
    const avgViewsPerSession = sessions ? (views / sessions).toFixed(1) : "0.0";
    const leadCount = leadRows.length;
    const conversion = sessions ? ((leadCount / sessions) * 100).toFixed(1) : "0.0";

    // Avg session duration: per session_id, take (max - min) of pageview timestamps.
    // Sessions with only one pageview contribute 0s (we still count them so the
    // average reflects bounce-heavy traffic honestly).
    const sessionTimes: Record<string, { min: number; max: number }> = {};
    for (const row of analyticsRows) {
      const key = row.session_id || row.id;
      const t = new Date(row.created_at).getTime();
      if (!sessionTimes[key]) sessionTimes[key] = { min: t, max: t };
      else {
        sessionTimes[key].min = Math.min(sessionTimes[key].min, t);
        sessionTimes[key].max = Math.max(sessionTimes[key].max, t);
      }
    }
    const durations = Object.values(sessionTimes).map((s) => (s.max - s.min) / 1000);
    const avgSeconds = durations.length
      ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
      : 0;
    const avgTime =
      avgSeconds >= 60
        ? `${Math.floor(avgSeconds / 60)}m ${avgSeconds % 60}s`
        : `${avgSeconds}s`;

    return { views, sessions, avgViewsPerSession, leadCount, conversion, avgTime };
  }, [analyticsRows, leadRows]);

  const byDay = useMemo(() => {
    const map: Record<string, { date: string; views: number; sessions: Set<string> }> = {};
    analyticsRows.forEach((row) => {
      const date = row.created_at.slice(5, 10);
      if (!map[date]) map[date] = { date, views: 0, sessions: new Set() };
      map[date].views += 1;
      map[date].sessions.add(row.session_id || row.id);
    });

    return Object.values(map)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((item) => ({ date: item.date, views: item.views, sessions: item.sessions.size }));
  }, [analyticsRows]);

  const topPages = useMemo(() => {
    const map: Record<string, number> = {};
    analyticsRows.forEach((row) => {
      map[row.path] = (map[row.path] || 0) + 1;
    });

    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([page, views]) => ({ page, views }));
  }, [analyticsRows]);

  const referrers = useMemo(() => {
    const map: Record<string, number> = {};
    analyticsRows.forEach((row) => {
      const key = referrerBucket(row.referrer);
      map[key] = (map[key] || 0) + 1;
    });

    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  }, [analyticsRows]);

  const leadsByDay = useMemo(() => {
    const map: Record<string, number> = {};
    leadRows.forEach((row) => {
      const date = row.created_at.slice(5, 10);
      map[date] = (map[date] || 0) + 1;
    });

    return Object.entries(map)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, leads]) => ({ date, leads }));
  }, [leadRows]);

  const speedSummary = useMemo(() => {
    const lcp = percentile(vitalRows.filter((row) => row.metric === "LCP").map((row) => row.value), 0.75);
    const inp = percentile(vitalRows.filter((row) => row.metric === "INP").map((row) => row.value), 0.75);
    const cls = percentile(vitalRows.filter((row) => row.metric === "CLS").map((row) => row.value), 0.75);
    return { lcp, inp, cls, samples: vitalRows.length };
  }, [vitalRows]);

  const failedLogins = securityEvents.filter(
    (e) => e.event_type?.toLowerCase().includes("fail") || e.severity === "high" || e.severity === "critical",
  ).length;
  const auditEventsCount = securityEvents.length;

  const reliability = useMemo(() => {
    const total = uptimeRows.length;
    const upCount = uptimeRows.filter((r) => r.is_up).length;
    const uptimePct = total ? (upCount / total) * 100 : null;
    const respVals = uptimeRows.map((r) => r.response_ms).filter((v): v is number => typeof v === "number");
    const avgResp = respVals.length ? Math.round(respVals.reduce((a, b) => a + b, 0) / respVals.length) : null;
    const latest = uptimeRows[0];
    const currentUp = latest ? latest.is_up : null;

    // Best SSL: pick the primary custom domain if we have it, else earliest expiry.
    const bySince: Record<string, SslRow> = {};
    for (const row of sslRows) {
      if (!bySince[row.domain]) bySince[row.domain] = row;
    }
    const preferred = bySince["homesteadconstruction.us"] ?? Object.values(bySince).sort((a, b) => (a.days_remaining ?? 9999) - (b.days_remaining ?? 9999))[0];
    return { uptimePct, avgResp, currentUp, total, ssl: preferred ?? null };
  }, [uptimeRows, sslRows]);

  const latestPsi = useMemo(() => {
    const mobile = pagespeedRows.find((r) => r.strategy === "mobile") ?? null;
    const desktop = pagespeedRows.find((r) => r.strategy === "desktop") ?? null;
    return { mobile, desktop };
  }, [pagespeedRows]);

  const runPagespeedNow = async () => {
    if (runningPsi) return;
    setRunningPsi(true);
    try {
      const res = await fetch("/api/public/hooks/check-pagespeed", { method: "POST" });
      if (!res.ok) throw new Error(await res.text());
      const { data: psi } = await sb
        .from("pagespeed_audits")
        .select("strategy, performance, accessibility, best_practices, seo, lcp_ms, cls, tbt_ms, checked_at")
        .order("checked_at", { ascending: false })
        .limit(20);
      setPagespeedRows((psi as PagespeedRow[]) ?? []);
    } catch (e) {
      console.error("PageSpeed run failed", e);
      alert("PageSpeed audit failed. Check server logs.");
    } finally {
      setRunningPsi(false);
    }
  };




  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-ink">Site Performance Report</h1>
          <p className="text-sm text-muted-foreground">
            A full overview of traffic, leads, speed, and security — the same shape as the report your client receives.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-card border border-border rounded-lg p-1">
            {RANGES.map((range) => (
              <button
                key={range.k}
                onClick={() => setDays(range.k)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  days === range.k ? "bg-brand text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {range.l}
              </button>
            ))}
          </div>
          <button
            disabled
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand text-primary-foreground text-sm font-semibold opacity-50 cursor-not-allowed"
          >
            <Wrench size={14} />
            Fix all (0)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi icon={Users} label="Visitors" value={stats.sessions} />
        <Kpi icon={Eye} label="Page views" value={stats.views} />
        <Kpi icon={MousePointerClick} label="Engagement" value={`${stats.avgViewsPerSession} pv/session`} />
        <Kpi icon={Clock} label="Avg. time" value={stats.avgTime} sub="per session" />
        <Kpi icon={Inbox} label="Leads" value={stats.leadCount} sub={`${leadRows.filter((row) => row.status === "new").length} new`} />
        <Kpi icon={TrendingUp} label="Conversion" value={`${stats.conversion}%`} sub="leads / visitors" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-ink">Traffic over time</h3>
            <span className="text-xs text-muted-foreground">Visitors & page views — last {days} days</span>
          </div>
          <div className="h-64">
            {byDay.length === 0 ? (
              <Empty label="No traffic data for this period yet." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={byDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="sessions" name="Visitors" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="views" name="Views" stroke="#a78bfa" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-display font-semibold text-ink mb-3 flex items-center gap-2">
            <Globe size={16} className="text-brand" /> Traffic sources
          </h3>
          <div className="h-64">
            {referrers.length === 0 ? (
              <Empty label="No referrer data yet." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={referrers} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {referrers.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-display font-semibold text-ink mb-3 flex items-center gap-2">
            <FileText size={16} className="text-brand" /> Top pages
          </h3>
          <div className="h-64">
            {topPages.length === 0 ? (
              <Empty label="No page views yet." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topPages} layout="vertical" margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis dataKey="page" type="category" stroke="hsl(var(--muted-foreground))" fontSize={11} width={140} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="views" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-ink flex items-center gap-2">
              <Inbox size={16} className="text-brand" /> Leads over time
            </h3>
            <Link to="/dashboard/form" className="text-xs text-brand hover:underline">
              View all →
            </Link>
          </div>
          <div className="h-64">
            {leadsByDay.length === 0 ? (
              <Empty label="No leads in this window yet." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leadsByDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="leads" fill="#34d399" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-ink">Recent leads</h3>
          <Link to="/dashboard/form" className="text-xs text-brand hover:underline">
            All submissions →
          </Link>
        </div>
        {leadRows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No submissions in this window.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 font-medium">Name</th>
                  <th className="py-2 font-medium">Email</th>
                  <th className="py-2 font-medium">Status</th>
                  <th className="py-2 font-medium text-right">When</th>
                </tr>
              </thead>
              <tbody>
                {leadRows.slice(0, 6).map((lead) => (
                  <tr key={lead.id} className="border-b border-border last:border-0">
                    <td className="py-2 font-medium text-ink">{lead.name || "—"}</td>
                    <td className="py-2 text-muted-foreground truncate max-w-[220px]">{lead.email || "—"}</td>
                    <td className="py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${lead.status === "new" ? "bg-brand/15 text-brand" : "bg-muted text-muted-foreground"}`}>
                        {lead.status || "unknown"}
                      </span>
                    </td>
                    <td className="py-2 text-right text-xs text-muted-foreground">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-ink flex items-center gap-2">
            <Activity size={16} className="text-brand" /> Reliability
          </h3>
          <span className="text-xs text-muted-foreground">Last 30 days</span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Activity size={13} className="text-brand" /> Current status
            </div>
            {reliability.currentUp === null ? (
              <p className="text-lg font-semibold text-muted-foreground">No data yet</p>
            ) : reliability.currentUp ? (
              <p className="text-lg font-semibold text-emerald-400 flex items-center gap-1.5">
                <CheckCircle2 size={18} /> Up
              </p>
            ) : (
              <p className="text-lg font-semibold text-red-400 flex items-center gap-1.5">
                <AlertTriangle size={18} /> Down
              </p>
            )}
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">30-day uptime</div>
            <p className="font-display text-2xl font-bold text-ink">
              {reliability.uptimePct === null ? "—" : `${reliability.uptimePct.toFixed(2)}%`}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{reliability.total} check{reliability.total === 1 ? "" : "s"}</p>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Avg response</div>
            <p className="font-display text-2xl font-bold text-ink">
              {reliability.avgResp === null ? "—" : `${reliability.avgResp} ms`}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Lock size={13} className="text-brand" /> SSL certificate
            </div>
            {reliability.ssl && reliability.ssl.days_remaining !== null && reliability.ssl.valid_to ? (
              <>
                <p className={`text-sm font-semibold ${reliability.ssl.days_remaining < 14 ? "text-amber-400" : "text-ink"}`}>
                  Certificate valid — expires in {reliability.ssl.days_remaining} days
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  on {new Date(reliability.ssl.valid_to).toLocaleDateString()}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Awaiting first check</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-display font-semibold text-ink flex items-center gap-2">
            <Gauge size={16} className="text-brand" /> PageSpeed Insights (Lighthouse)
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {latestPsi.mobile || latestPsi.desktop
                ? `Last run ${new Date((latestPsi.mobile ?? latestPsi.desktop)!.checked_at).toLocaleString()}`
                : "No audits yet"}
            </span>
            <button
              onClick={runPagespeedNow}
              disabled={runningPsi}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-brand text-primary-foreground text-xs font-semibold disabled:opacity-50"
            >
              <Gauge size={13} />
              {runningPsi ? "Running…" : "Run PageSpeed audit now"}
            </button>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {(["mobile", "desktop"] as const).map((strategy) => {
            const row = strategy === "mobile" ? latestPsi.mobile : latestPsi.desktop;
            return (
              <div key={strategy} className="rounded-lg border border-border p-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">{strategy}</div>
                {row ? (
                  <>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {[
                        { label: "Perf", v: row.performance },
                        { label: "A11y", v: row.accessibility },
                        { label: "Best", v: row.best_practices },
                        { label: "SEO", v: row.seo },
                      ].map(({ label, v }) => (
                        <div key={label} className="text-center">
                          <p className={`font-display text-2xl font-bold ${v === null ? "text-muted-foreground" : v >= 90 ? "text-emerald-400" : v >= 50 ? "text-amber-400" : "text-red-400"}`}>
                            {v ?? "—"}
                          </p>
                          <p className="text-[10px] text-muted-foreground">{label}</p>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-muted-foreground">LCP</span> <span className="text-ink font-medium">{row.lcp_ms ? `${(row.lcp_ms / 1000).toFixed(2)}s` : "—"}</span></div>
                      <div><span className="text-muted-foreground">CLS</span> <span className="text-ink font-medium">{row.cls !== null ? Number(row.cls).toFixed(3) : "—"}</span></div>
                      <div><span className="text-muted-foreground">TBT</span> <span className="text-ink font-medium">{row.tbt_ms !== null ? `${row.tbt_ms}ms` : "—"}</span></div>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No {strategy} audit yet.</p>
                )}
              </div>
            );
          })}
        </div>
      </div>


      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">

        <Link to="/dashboard/admin/security" className="block p-5 rounded-xl border border-border bg-card hover:border-brand/40 transition-colors">
          <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            <ShieldCheck size={16} className="text-brand" />
            <span className="font-medium">Security Audit</span>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-display font-bold text-ink">{failedLogins}</span>
            <span className="text-xs text-muted-foreground mb-1">flagged</span>
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            {auditEventsCount === 0
              ? "No security events recorded in this window."
              : `${auditEventsCount} total event${auditEventsCount === 1 ? "" : "s"} logged · last ${days} days`}
          </div>
        </Link>

        <Link to="/dashboard/admin/security" className="block p-5 rounded-xl border border-border bg-card hover:border-brand/40 transition-colors">
          <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            <Shield size={16} className="text-brand" />
            <span className="font-medium">Security Posture</span>
          </div>
          <div className="flex items-center gap-2">
            {failedLogins > 0 ? <AlertTriangle className="text-amber-400" size={22} /> : <CheckCircle2 className="text-emerald-400" size={22} />}
            <span className="text-lg font-semibold text-ink">{failedLogins > 0 ? "Review recent activity" : "No recent alerts"}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-2">Sign-in events from the selected range</div>
        </Link>

        <Link to="/dashboard/admin/speed" className="block p-5 rounded-xl border border-border bg-card hover:border-brand/40 transition-colors">
          <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            <Gauge size={16} className="text-brand" />
            <span className="font-medium">Page Speed</span>
          </div>
          <div className="flex items-center gap-2">
            {speedSummary.samples > 0 ? <CheckCircle2 className="text-emerald-400" size={22} /> : <Gauge className="text-muted-foreground" size={22} />}
            <span className="text-lg font-semibold text-ink">{speedSummary.samples > 0 ? "Speed data available" : "Awaiting visitor data"}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            {speedSummary.samples > 0
              ? `LCP ${(speedSummary.lcp / 1000).toFixed(2)}s · CLS ${speedSummary.cls.toFixed(2)} · INP ${Math.round(speedSummary.inp)}ms`
              : "Visit the live site to start collecting measurements"}
          </div>
        </Link>

        <Link to="/dashboard/admin/reports" className="block p-5 rounded-xl border border-border bg-card hover:border-brand/40 transition-colors">
          <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            <TrendingUp size={16} className="text-brand" />
            <span className="font-medium">GA4 Reports</span>
          </div>
          <div className="text-lg font-semibold text-ink">Open full report</div>
          <div className="text-xs text-muted-foreground mt-2">Connection health and reporting layout</div>
        </Link>
      </div>

      {loading && <div className="text-xs text-muted-foreground">Refreshing…</div>}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub }: { icon: any; label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        <Icon size={14} className="text-brand" />
        <span>{label}</span>
      </div>
      <p className="font-display text-2xl font-bold text-ink">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="h-full flex items-center justify-center text-sm text-muted-foreground">{label}</div>;
}
